'use client';
import { Alert, Link } from '@magicschool/spellbook';

import { useStore } from 'features/store';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type TosUpdateBannerProps = {
  onDismiss?: () => void;
};

const TosUpdateBanner = ({ onDismiss }: TosUpdateBannerProps) => {
  const intl = useIntl();
  const hasLoadedRef = useRef(false);

  const {
    activeBanner,
    showBanner,
    closeBanner,
    load,
    handleUnload: storeHandleUnload,
  } = useStore(
    ({ ToSBannerStoreData: s }) => ({
      activeBanner: s.activeBanner,
      showBanner: s.showBanner,
      load: s.load,
      closeBanner: s.closeBanner,
      handleUnload: s.handleUnload,
    }),
    [],
  );

  const handleDismiss = useCallback(() => {
    closeBanner();
    onDismiss?.();
  }, [closeBanner, onDismiss]);

  const handleUnload = useCallback(() => {
    storeHandleUnload();
    onDismiss?.();
  }, [storeHandleUnload, onDismiss]);

  // Load ToS updates only once per component lifecycle
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      load();
    }
  }, []); // Empty dependency array - only run once on mount

  // Handle beforeunload event separately
  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload, true);
    return () => {
      window.removeEventListener('beforeunload', handleUnload, true);
    };
  }, [handleUnload]);

  useEffect(() => {
    if (activeBanner) onDismiss?.();
    else onDismiss?.();
  }, [activeBanner, onDismiss]);

  return !showBanner || !activeBanner ? null : (
    // Announced by the PageBannerRegion this banner renders in.
    <Alert
      role="presentation"
      onDismiss={handleDismiss}
      status="warning"
      variant="banner"
      text={{ dismissButtonLabel: intl.formatMessage({ id: 'close', defaultMessage: 'Close' }) }}
    >
      <Alert.Content>
        <FormattedMessage
          id={activeBanner.messageId}
          values={{
            privacyA: (text) => (
              <Link
                key="privacyA"
                href="https://www.magicschool.ai/privacy"
                onClick={handleDismiss}
                showExternalIcon={false}
                target="_blank"
                text={{ externalText: intl.formatMessage({ id: 'link.external' }) }}
              >
                {text}
              </Link>
            ),
          }}
        />
      </Alert.Content>
    </Alert>
  );
};

export default TosUpdateBanner;
